<template>
  <section class="lab-session__container">
    <div v-show="displayStartLabCard" class="lab-session__wrapper">
      <CardCurrentSession
        ref="currentLabSession"
        class="lab-session__session-card"
        v-bind="{
          loading,
          loadingLabel,
          parentId,
          parentType,
          simpleCard,
          labError,
        }"
        @close-modal="toggleLab"
        @update-session-status="startAssessmentHandler"
        @create-session="createSession"
      >
        <template slot="title">
          <slot name="title"></slot>
        </template>
        <template slot="ctaHandler">
          <slot name="ctaHandler"></slot>
        </template>
        <template slot="info">
          <slot name="info"></slot>
        </template>
      </CardCurrentSession>
    </div>
    <LabIframe v-show="!displayStartLabCard" class="lab-session__iframe" />
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import CONSTANTS from '@/constants'
import CardCurrentSession from './CardCurrentSession.vue'
import LabIframe from './LabIframe.vue'
import { labHandlerCommons } from '@/helpers/mixins'

export default {
  name: 'LabSession',
  components: {
    CardCurrentSession,
    LabIframe,
  },
  mixins: [labHandlerCommons],
  props: {
    parentId: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: true,
    },
    labId: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    showStartLabCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    simpleCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    fetchLab: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      labError: false,
      loadingText: '',
    }
  },
  computed: {
    ...mapState({
      currentLab: state => state.labs.currentLab,
      currentLabSession: state => state.labs.currentLabSession,
    }),
    ...mapGetters({
      isRestartingLab: 'labs/isRestartingLab',
      isSwitchingLabs: 'labs/isSwitchingLabs',
      isChangingLabState: 'labs/isChangingLabState',
      isFetchingLab: 'labs/isFetchingLab',
      isTransitioningStates: 'labs/isTransitioningStates',
      currentLabSessionStatus: 'labs/currentLabSessionStatus',
    }),
    loadingLabel() {
      if (this.loadingText) return this.loadingText
      return this.simpleCard &&
        this.currentLabSessionStatus ===
          CONSTANTS.LABS.SESSION_STATUS.TERMINATING
        ? 'You have a previous lab session ending. Please wait a few seconds'
        : ''
    },
    loading() {
      const loadingFlags = [
        this.isChangingLabState,
        this.isTransitioningStates,
        this.isFetchingLab,
        this.isLoading,
      ]
      return loadingFlags.includes(true)
    },
    displayStartLabCard() {
      return this.showStartLabCard || this.loading
    },
  },
  watch: {
    isRestartingLab: {
      immediate: true,
      handler(value) {
        if (value) this.switchLabs()
      },
    },
    isSwitchingLabs: {
      immediate: true,
      handler(value) {
        if (value) this.switchLabs()
      },
    },
  },
  async created() {
    if (this.fetchLab) await this.fetchContent()
  },
  methods: {
    ...mapActions('labs', ['updateLabStatus']),
    async startAssessmentHandler() {
      const { global_running_session } = this.currentLab
      if (
        this.currentLabSessionStatus ===
          CONSTANTS.LABS.SESSION_STATUS.STARTED &&
        global_running_session &&
        global_running_session.id === this.currentLabSession?.id
      ) {
        return this.$emit('session-started', true)
      }
    },
    async createSession(payload) {
      const { parentId, parentType, regionSelected, keyboardSelected } = payload
      try {
        this.updateLabStatus({ isStartingLab: true })
        const payload = {
          id: this.labId,
          region: regionSelected,
          keyboard_layout: keyboardSelected?.code,
          parentId,
          parentType,
        }
        this.$emit('session-started', true)
        await this.$store.dispatch('labs/createLabSession', payload)
      } catch (error) {
        this.updateLabStatus({ isStartingLab: false })
      } finally {
        this.updateLabStatus({ isStartingLab: false })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.lab-session {
  width: 100%;
  height: 100%;
  min-height: 280px;
  border-top: 1px solid var(--tundora-gray);
  &__wrapper {
    height: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__container {
    width: 100%;
    height: 100%;
  }
  &__session-card {
    height: 570px;
  }
}
</style>
